import api from '@/services/api';

export interface User {
  _id: string;
  name: string;
  email: string;
  username: string;
  role: 'user' | 'admin';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  role?: 'user' | 'admin';
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  role?: 'user' | 'admin';
  isActive?: boolean;
}

export interface UserStats {
  total: number;
  active: number;
  inactive: number;
  admins: number;
  regularUsers: number;
  recentUsers: number;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'user' | 'admin';
  isActive?: boolean;
}

export class UserService {

  // Ottenere tutti gli utenti con paginazione e filtri
  static async getAllUsers(params: UsersQueryParams = {}) {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/users?${queryString}` : '/users';

    const response = await api.get<{
      success: boolean;
      data: UsersResponse;
    }>(url);

    return response.data.data;
  }

  // Ottenere un utente specifico
  static async getUserById(id: string) {
    const response = await api.get<{
      success: boolean;
      data: { user: User };
    }>(`/users/${id}`);

    return response.data.data.user;
  }

  // Creare un nuovo utente
  static async createUser(userData: CreateUserData) {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: { user: User };
    }>('/users', userData);

    return response.data.data.user;
  }

  // Aggiornare un utente
  static async updateUser(id: string, userData: UpdateUserData) {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: { user: User };
    }>(`/users/${id}`, userData);

    return response.data.data.user;
  }

  // Aggiornare password utente
  static async updateUserPassword(id: string, password: string) {
    const response = await api.put<{
      success: boolean;
      message: string;
    }>(`/users/${id}/password`, { password });

    return response.data;
  }

  // Eliminare un utente
  static async deleteUser(id: string) {
    const response = await api.delete<{
      success: boolean;
      message: string;
    }>(`/users/${id}`);

    return response.data;
  }

  // Attivare/disattivare un utente
  static async toggleUserActive(id: string) {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: { user: User };
    }>(`/users/${id}/toggle-active`);

    return response.data;
  }

  // Ottenere statistiche utenti
  static async getUserStats() {
    const response = await api.get<{
      success: boolean;
      data: { stats: UserStats };
    }>('/users/stats');

    return response.data.data.stats;
  }
}